import { useState, useCallback, useMemo } from 'react'
import { Button } from '#app/components/ui/button.js'
import { Input } from '#app/components/ui/input.js'
import { Surface } from '../../ui/Surface'

export type LinkEditorPanelProps = {
	initialUrl?: string
	initialOpenInNewTab?: boolean
	onSetLink: (url: string, openInNewTab?: boolean) => void
}

export const useLinkEditorState = ({
	initialUrl,
	initialOpenInNewTab,
	onSetLink,
}: LinkEditorPanelProps) => {
	const [url, setUrl] = useState(initialUrl || '')
	const [openInNewTab, setOpenInNewTab] = useState(initialOpenInNewTab || false)

	const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setUrl(event.target.value)
	}, [])

	const isValidUrl = useMemo(() => /^(\S+):(\/\/)?\S+$/.test(url), [url])

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault()
			if (isValidUrl) {
				onSetLink(url, openInNewTab)
			}
		},
		[url, isValidUrl, openInNewTab, onSetLink],
	)

	return {
		url,
		setUrl,
		openInNewTab,
		setOpenInNewTab,
		onChange,
		handleSubmit,
		isValidUrl,
	}
}

export const LinkEditorPanel = ({
	onSetLink,
	initialOpenInNewTab,
	initialUrl,
}: LinkEditorPanelProps) => {
	const state = useLinkEditorState({
		onSetLink,
		initialOpenInNewTab,
		initialUrl,
	})

	return (
		<Surface>
			<form onSubmit={state.handleSubmit} className="flex items-center gap-1">
				<Input
					type="url"
					placeholder="https://"
					variant="s"
					icon="Link2"
					value={state.url}
					onChange={state.onChange}
				/>
				<Button
					size="s"
					type="submit"
					disabled={!state.isValidUrl}
					className="shrink-0"
				>
					Set Link
				</Button>
			</form>
		</Surface>
	)
}
